import { useContext } from "react";

import {
  CalculateContext,
  TCostingCalculate,
  TElement,
  TTotalCosting,
  TTotalCostingCleaning,
} from "@/modules/dwelling/containers/DwellingEstimateContainer/Calculate/CalculateContext.ts";
import {
  Costing,
  DwellingCostCleaningPlan,
  DwellingCostDilapidationsResponse,
  DwellingCostResponse,
  EquipmentPlan,
  OccupationPeriod,
} from "@/modules/dwelling/containers/DwellingEstimateContainer/types.ts";

export const useCalculate = () => {
  const fixe = 2;

  const { costingCalculate, setCostingCalculate } =
    useContext(CalculateContext);

  const convertOccupationPeriodToFractionalYears = (
    period: OccupationPeriod
  ): number => {
    const fractionalMonth = period.months / 12;
    return Number(period.years) + Number(fractionalMonth);
  };

  const totalTTC = ({
    quantity,
    preTaxPrice,
    taxPercentage,
    dilapidation,
  }: Costing) => {
    const dilapidationPercentage = dilapidation?.rate / 100 || 1;
    const totalWithoutDilapidation =
      preTaxPrice * quantity * dilapidationPercentage;

    return Number(totalWithoutDilapidation * (1 + taxPercentage / 100)).toFixed(
      fixe
    );
  };

  const totalElement = (newCostingCalculate: TElement): TTotalCosting => {
    const totalRow: TTotalCosting = {
      preTaxPrice: 0,
      dilapidationDeducted: 0,
      taxAmount: 0,
      total: 0,
    };

    newCostingCalculate?.rows?.map((row, index) => {
      if (newCostingCalculate.selected[index]) {
        totalRow.preTaxPrice += row.preTaxPrice * row.quantity;
        const dilapidationPercentage = row?.dilapidation?.rate / 100 || 1;

        const dilapidationPrice =
          row.preTaxPrice * row.quantity * dilapidationPercentage;

        totalRow.dilapidationDeducted += Number(
          (row.preTaxPrice * row.quantity - dilapidationPrice).toFixed(fixe)
        );

        totalRow.taxAmount += Number(
          (row.taxIncludedPrice - dilapidationPrice).toFixed(fixe)
        );
        totalRow.total += Number(Number(row.taxIncludedPrice).toFixed(fixe));
      }
    });
    return totalRow;
  };

  // Get cleaning totals from an element
  const getCleaningTotals = (cleaningElement: TElement) => {
    return {
      preTaxPrice: cleaningElement.total.preTaxPrice,
      taxAmount: cleaningElement.total.taxAmount,
      total: cleaningElement.total.total,
    };
  };

  // Calculate overall totals from all elements
  const calculateOverallTotals = (elements: Record<string, TElement>) => {
    let preTaxPrice = 0;
    let dilapidationDeducted = 0;
    let taxAmount = 0;
    let total = 0;

    for (const key in elements) {
      if (Object.prototype.hasOwnProperty.call(elements, key)) {
        const element = elements[key];
        preTaxPrice += element.total.preTaxPrice;
        dilapidationDeducted += element.total.dilapidationDeducted;
        taxAmount += element.total.taxAmount;
        total += element.total.total;
      }
    }

    return {
      preTaxPrice,
      dilapidationDeducted,
      taxAmount,
      total,
    };
  };

  const totalCosting = (
    newCostingCalculate: TElement,
    name?: string,
    index?: number
  ): TTotalCosting & TTotalCostingCleaning => {
    const total: TTotalCosting & TTotalCostingCleaning = {
      preTaxPrice: 0,
      dilapidationDeducted: 0,
      taxAmount: 0,
      total: 0,
      cleaning: {
        preTaxPrice: 0,
        taxAmount: 0,
        total: 0,
      },
    };

    // Process all other elements
    for (const indexElement in costingCalculate.elements) {
      if (indexElement === "cleanings") {
        // Skip cleanings for now, will handle separately below
        continue;
      }

      if (
        indexElement === name &&
        index !== undefined &&
        newCostingCalculate.selected[index]
      ) {
        total.preTaxPrice += newCostingCalculate.total.preTaxPrice;
        total.dilapidationDeducted +=
          newCostingCalculate.total.dilapidationDeducted;
        total.taxAmount += newCostingCalculate.total.taxAmount;
        total.total += newCostingCalculate.total.total;
      } else {
        total.preTaxPrice +=
          costingCalculate.elements[indexElement].total.preTaxPrice;
        total.dilapidationDeducted +=
          costingCalculate.elements[indexElement].total.dilapidationDeducted;
        total.taxAmount +=
          costingCalculate.elements[indexElement].total.taxAmount;
        total.total += costingCalculate.elements[indexElement].total.total;
      }
    }

    // Special handling for the cleanings element to ensure it's properly calculated
    if (name === "cleanings" && index !== undefined) {
      // If we're updating a cleaning element row, use the new calculated totals
      const cleaningElement = newCostingCalculate;
      total.cleaning = getCleaningTotals(cleaningElement);

      // Also update the global totals
      total.preTaxPrice += cleaningElement.total.preTaxPrice;
      total.taxAmount += cleaningElement.total.taxAmount;
      total.total += cleaningElement.total.total;
    } else {
      // Otherwise use the existing cleanings totals
      const cleaningElement = costingCalculate.elements["cleanings"];
      total.cleaning = getCleaningTotals(cleaningElement);

      // Add to global totals
      total.preTaxPrice += cleaningElement.total.preTaxPrice;
      total.taxAmount += cleaningElement.total.taxAmount;
      total.total += cleaningElement.total.total;
    }

    return total;
  };

  const handleCountRepair = (name: string): number => {
    return costingCalculate?.elements[name]?.rows?.reduce(
      (acc, _, unitIndex) => {
        return costingCalculate.elements[name].selected[unitIndex]
          ? acc + 1
          : acc;
      },
      0
    );
  };

  const updateElement = (
    name: string,
    index: number,
    newCostingCalculateElement: TElement
  ) => {
    const element = newCostingCalculateElement.rows[index];

    newCostingCalculateElement.rows[index].taxIncludedPrice = Number(
      totalTTC(newCostingCalculateElement.rows[index])
    );

    const dilapidationPercentage = element?.dilapidation?.rate / 100 || 1;

    const dilapidationPrice = Number(
      (element.preTaxPrice * element.quantity * dilapidationPercentage).toFixed(
        fixe
      )
    );

    newCostingCalculateElement.rows[index].taxAmount = Number(
      (element.taxIncludedPrice - dilapidationPrice).toFixed(fixe)
    );
    newCostingCalculateElement.total = totalElement(newCostingCalculateElement);
    const total = totalCosting(newCostingCalculateElement, name, index);

    setCostingCalculate((v) => ({
      occupationPeriod: v.occupationPeriod,
      elements: {
        ...v.elements,
        [name]: newCostingCalculateElement,
      },
      total,
    }));
  };

  // Update the costingCalculate with a specific element and recalculate totals
  const updateCostingCalculateWithElement = (
    name: string,
    element: TElement
  ) => {
    const updatedCostingCalculate = {
      ...costingCalculate,
      elements: {
        ...costingCalculate.elements,
        [name]: element,
      },
    };

    if (name === "cleanings") {
      const cleaningTotals = getCleaningTotals(element);
      const overallTotals = calculateOverallTotals(
        updatedCostingCalculate.elements
      );

      updatedCostingCalculate.total = {
        ...overallTotals,
        cleaning: cleaningTotals,
      };
    } else {
      updatedCostingCalculate.total = totalCosting(element, name);
    }

    return updatedCostingCalculate;
  };

  const updateAllElements = (newCostingCalculate: TCostingCalculate) => {
    const keyElements = [...Object.keys(newCostingCalculate.elements)];

    // First, recalculate all row values for each element
    keyElements?.forEach((key) => {
      newCostingCalculate.elements[key]?.rows?.forEach((row) => {
        row.taxIncludedPrice = Number(totalTTC(row));

        const dilapidationPercentage = row?.dilapidation?.rate / 100 || 1;
        const dilapidationPrice =
          row.preTaxPrice * row.quantity * dilapidationPercentage;

        row.taxAmount = Number(
          (row.taxIncludedPrice - dilapidationPrice).toFixed(fixe)
        );
      });

      // Update the total for each element
      newCostingCalculate.elements[key].total = totalElement(
        newCostingCalculate.elements[key]
      );
    });

    // Get cleaning totals
    const cleaningTotals = newCostingCalculate.elements["cleanings"]
      ? getCleaningTotals(newCostingCalculate.elements["cleanings"])
      : { preTaxPrice: 0, taxAmount: 0, total: 0 };

    // Calculate overall totals
    const overallTotals = calculateOverallTotals(newCostingCalculate.elements);

    newCostingCalculate.total = {
      ...overallTotals,
      cleaning: cleaningTotals,
    };

    setCostingCalculate({ ...newCostingCalculate });
  };

  const handleChangeQuantity = (name: string, index: number, value: string) => {
    const newCostingCalculate = costingCalculate.elements[name];
    newCostingCalculate.rows[index].quantity = Number(value);
    updateElement(name, index, newCostingCalculate);
  };

  const handleChangePreTaxPrice = (
    name: string,
    index: number,
    value: string
  ) => {
    const newCostingCalculate = costingCalculate.elements[name];

    newCostingCalculate.rows[index].preTaxPrice = Number(value);
    updateElement(name, index, newCostingCalculate);
  };

  const handleChangeUnit = (name: string, index: number, value: string) => {
    const newPlan = costingCalculate.elements[name];
    newPlan.rows[index].plan = {
      description: newPlan.rows[index]?.plan?.description || "",
      id: newPlan.rows[index]?.plan?.id || "",
      label: newPlan.rows[index]?.plan?.label || "",
      slug: newPlan.rows[index]?.plan?.slug || "",
      types: newPlan.rows[index]?.plan?.types || "",
      unit: value,
    };
    updateElement(name, index, newPlan);
  };

  const handleChangeTaxPercentage = (
    name: string,
    index: number,
    value: string
  ) => {
    const newCostingCalculate = costingCalculate.elements[name];

    newCostingCalculate.rows[index].taxPercentage = Number(value);
    updateElement(name, index, newCostingCalculate);
  };

  const handleChangeDilapidationPercentage = (
    name: string,
    index: number,
    value: string
  ) => {
    const newCostingCalculate = costingCalculate.elements[name];

    newCostingCalculate.rows[index].dilapidation.rate = Number(value);
    updateElement(name, index, newCostingCalculate);
  };

  const handleChangeSelect = (
    name: string,
    index: number,
    plan: EquipmentPlan | DwellingCostCleaningPlan
  ) => {
    const newCostingCalculate = costingCalculate.elements[name];

    newCostingCalculate.rows[index].preTaxPrice = plan.preTaxPrice;
    newCostingCalculate.rows[index].taxPercentage = plan.taxPercentage;
    newCostingCalculate.rows[index].plan = {
      slug: plan.slug,
      types: "",
      id: plan.id,
      label: plan.label,
      description: plan.description,
      unit: plan.unit,
    };

    updateElement(name, index, newCostingCalculate);
  };

  const handleChangeSelectRow = (name: string, index: number) => {
    const newCostingCalculate = { ...costingCalculate.elements[name] };
    newCostingCalculate.selected[index] = !newCostingCalculate.selected[index];

    // Recalculate the element's total
    newCostingCalculate.total = totalElement(newCostingCalculate);

    // Update costingCalculate with the new element and recalculated totals
    const updatedCostingCalculate = updateCostingCalculateWithElement(
      name,
      newCostingCalculate
    );

    setCostingCalculate(updatedCostingCalculate);
  };

  const unselectEveryRow = (name: string) => {
    const newCostingCalculate = { ...costingCalculate.elements[name] };
    let anyChanges = false;

    // Unselect all rows except the last one
    for (const index in newCostingCalculate.selected) {
      const i = Number(index);
      if (i !== newCostingCalculate.selected.length - 1) {
        if (newCostingCalculate.selected[i]) {
          newCostingCalculate.selected[i] = false;
          anyChanges = true;
        }
      }
    }

    if (!anyChanges) return; // No changes made, return early

    // Recalculate the totals for this element
    newCostingCalculate.total = totalElement(newCostingCalculate);

    // Update costingCalculate with the new element and recalculated totals
    const updatedCostingCalculate = updateCostingCalculateWithElement(
      name,
      newCostingCalculate
    );

    setCostingCalculate(updatedCostingCalculate);
  };

  const unselectAllDwelling = (name: string) => {
    if (name !== "cleanings") return; // Only applicable to the cleanings section

    const newCostingCalculate = { ...costingCalculate.elements[name] };
    const allDwellingIndex = newCostingCalculate.selected.length - 1;

    // If it's already unselected, do nothing
    if (!newCostingCalculate.selected[allDwellingIndex]) return;

    // Unselect the all-dwelling option
    newCostingCalculate.selected[allDwellingIndex] = false;

    // Recalculate totals
    newCostingCalculate.total = totalElement(newCostingCalculate);

    // Update costingCalculate with the new element and recalculated totals
    const updatedCostingCalculate = updateCostingCalculateWithElement(
      name,
      newCostingCalculate
    );

    setCostingCalculate(updatedCostingCalculate);
  };

  const handleChangeDuration = (
    occupationPeriod: OccupationPeriod,
    dilapidations: DwellingCostDilapidationsResponse
  ) => {
    const newCostingCalculate = costingCalculate;
    newCostingCalculate.occupationPeriod = occupationPeriod;

    const keyElements = [...Object.keys(newCostingCalculate.elements)];

    keyElements?.map((key) => {
      newCostingCalculate.elements[key]?.rows?.map((row) => {
        if (row?.dilapidation?.slug) {
          const dilapidation = dilapidations.find((dilapidation) => {
            const occupation =
              convertOccupationPeriodToFractionalYears(occupationPeriod);
            return (
              dilapidation.slug === row.dilapidation?.slug &&
              dilapidation.period &&
              dilapidation.period <= occupation
            );
          });
          row.dilapidation.rate = dilapidation?.rate || 100;
        }
      });
    });

    updateAllElements(newCostingCalculate);
  };

  const init = (data: DwellingCostResponse): TCostingCalculate => {
    return {
      elements: {
        key: {
          rows: data?.key?.units?.map((unit) => ({
            ...unit.costing,
          })),
          selected: data?.key?.units?.map((unit) => unit.isSelected),
          total: {
            preTaxPrice: data?.key?.roomCosting?.preTaxPrice || 0,
            dilapidationDeducted:
              data?.key?.roomCosting?.dilapidationDeducted || 0,
            taxAmount: data?.key?.roomCosting?.taxAmount || 0,
            total: data?.key?.roomCosting?.total || 0,
          },
        },
        equipment: {
          rows: data?.equipment?.units?.map((unit) => ({
            ...unit.costing,
          })),
          selected: data?.equipment?.units?.map((unit) => unit.isSelected),
          total: {
            preTaxPrice: data?.equipment?.roomCosting?.preTaxPrice || 0,
            dilapidationDeducted:
              data?.equipment?.roomCosting?.dilapidationDeducted || 0,
            taxAmount: data?.equipment?.roomCosting?.taxAmount || 0,
            total: data?.equipment?.roomCosting?.total || 0,
          },
        },
        cleanings: {
          // @ts-expect-error not exactly the same type
          rows: data.cleanings.map((cleaning) => ({
            ...cleaning.costing,
            plan: { ...cleaning.plan },
          })),
          selected: data.cleanings.map((cleaning) => cleaning.isSelected),
          total: {
            preTaxPrice: data.totalCosting.cleaning.preTaxPrice,
            dilapidationDeducted: 0,
            taxAmount: data.totalCosting.cleaning.taxAmount,
            total: data.totalCosting.cleaning.total,
          },
        },
        ...data.rooms.reduce(
          (acc, room) => ({
            ...acc,
            [room.id]: {
              rows: room.units.map((unit) => ({
                ...unit.costing,
              })),
              selected: room.units.map((unit) => unit.isSelected),
              total: {
                preTaxPrice: room.roomCosting.preTaxPrice,
                dilapidationDeducted: room.roomCosting.dilapidationDeducted,
                taxAmount: room.roomCosting.taxAmount,
                total: room.roomCosting.total,
              },
            },
          }),
          {}
        ),
      },
      occupationPeriod: data.dwelling.occupationPeriod,
      total: {
        preTaxPrice: data.totalCosting.preTaxPrice,
        dilapidationDeducted: data.totalCosting.dilapidationDeducted,
        taxAmount: data.totalCosting.taxAmount,
        total: data.totalCosting.total,
        cleaning: {
          preTaxPrice: data.totalCosting.cleaning.preTaxPrice,
          taxAmount: data.totalCosting.cleaning.taxAmount,
          total: data.totalCosting.cleaning.total,
        },
      },
    };
  };

  return {
    handleChangeQuantity,
    handleChangePreTaxPrice,
    handleChangeTaxPercentage,
    handleChangeDilapidationPercentage,
    handleChangeSelect,
    handleChangeSelectRow,
    handleChangeDuration,
    unselectEveryRow,
    unselectAllDwelling,
    costingCalculate,
    handleCountRepair,
    handleChangeUnit,
    init,
  };
};
